var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { translate } from '@utils/i18n';
import { createComponentType, updateComponentType, toggleComponentTypeStatus, getComponentTypes, getCategories, getAllowedAttachableTypes, getRecommendedComponentTypes, importRecommendedComponentType } from '../services/ComponentTypesService';
const namespace = 'features.settings.component_types';
// Query Keys
export const COMPONENT_TYPES_QUERY_KEYS = {
    all: ['componentTypes'],
    list: () => ['componentTypes', 'list'],
    categories: () => ['componentTypes', 'categories'],
    allowedAttachableTypes: () => ['componentTypes', 'allowedAttachableTypes'],
    recommended: () => ['componentTypes', 'recommended']
};
// Hook to fetch component types
export function useComponentTypes() {
    return useQuery({
        queryKey: COMPONENT_TYPES_QUERY_KEYS.list(),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getComponentTypes();
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data || [];
        }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3
    });
}
// Hook to fetch component categories
export function useComponentCategories() {
    return useQuery({
        queryKey: COMPONENT_TYPES_QUERY_KEYS.categories(),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getCategories();
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data || [];
        }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3
    });
}
// Hook to fetch allowed attachable types
export function useAllowedAttachableTypes() {
    return useQuery({
        queryKey: COMPONENT_TYPES_QUERY_KEYS.allowedAttachableTypes(),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getAllowedAttachableTypes();
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data || [];
        }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3
    });
}
// Hook to create a component type
export function useCreateComponentTypeMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => __awaiter(this, void 0, void 0, function* () {
            const response = yield createComponentType(data);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (newType) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            // Cancel any outgoing refetches to avoid overwriting our optimistic update
            yield queryClient.cancelQueries({ queryKey: COMPONENT_TYPES_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousTypes = queryClient.getQueryData(COMPONENT_TYPES_QUERY_KEYS.list());
            // Optimistically update to the new value with temporary ID
            if (previousTypes) {
                const optimisticType = {
                    id: Date.now(), // Temporary ID
                    typeName: newType.typeNameEn,
                    typeNameEn: newType.typeNameEn,
                    typeNameEs: newType.typeNameEs || null,
                    typeNameFr: newType.typeNameFr || null,
                    description: newType.description || null,
                    category: newType.category || null,
                    active: (_a = newType.active) !== null && _a !== void 0 ? _a : true,
                    fieldDefinitions: newType.fieldDefinitions || null,
                    componentsCount: 0,
                    fieldsCount: ((_c = (_b = newType.fieldDefinitions) === null || _b === void 0 ? void 0 : _b.fields) === null || _c === void 0 ? void 0 : _c.length) || 0,
                    editUrl: '',
                    deleteUrl: '',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    supportsIndividualReports: (_d = newType.supportsIndividualReports) !== null && _d !== void 0 ? _d : false
                };
                queryClient.setQueryData(COMPONENT_TYPES_QUERY_KEYS.list(), [...previousTypes, optimisticType]);
            }
            // Return a context object with the snapshotted value
            return { previousTypes };
        }),
        onSuccess: () => {
            toast.success('Component type created successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_TYPES_QUERY_KEYS.all
            });
        },
        onError: (error, _newType, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousTypes) {
                queryClient.setQueryData(COMPONENT_TYPES_QUERY_KEYS.list(), context.previousTypes);
            }
            toast.error(error.message || 'Failed to create component type. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_TYPES_QUERY_KEYS.all
            });
        }
    });
}
// Hook to update a component type
export function useUpdateComponentTypeMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ id, data }) {
            const response = yield updateComponentType(id, data);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ id, data }) {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries({ queryKey: COMPONENT_TYPES_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousTypes = queryClient.getQueryData(COMPONENT_TYPES_QUERY_KEYS.list());
            // Optimistically update to the new value
            if (previousTypes) {
                const updatedTypes = previousTypes.map((type) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
                    return type.id === id
                        ? Object.assign(Object.assign({}, type), { typeName: (_a = data.typeNameEn) !== null && _a !== void 0 ? _a : type.typeName, typeNameEn: (_b = data.typeNameEn) !== null && _b !== void 0 ? _b : type.typeNameEn, typeNameEs: (_c = data.typeNameEs) !== null && _c !== void 0 ? _c : type.typeNameEs, typeNameFr: (_d = data.typeNameFr) !== null && _d !== void 0 ? _d : type.typeNameFr, description: (_e = data.description) !== null && _e !== void 0 ? _e : type.description, category: (_f = data.category) !== null && _f !== void 0 ? _f : type.category, active: (_g = data.active) !== null && _g !== void 0 ? _g : type.active, compatibleChildTypeIds: (_h = data.compatibleChildTypeIds) !== null && _h !== void 0 ? _h : type.compatibleChildTypeIds, allowedAttachableTypes: (_j = data.allowedAttachableTypes) !== null && _j !== void 0 ? _j : type.allowedAttachableTypes, fieldDefinitions: (_k = data.fieldDefinitions) !== null && _k !== void 0 ? _k : type.fieldDefinitions, fieldsCount: (_o = (_m = (_l = data.fieldDefinitions) === null || _l === void 0 ? void 0 : _l.fields) === null || _m === void 0 ? void 0 : _m.length) !== null && _o !== void 0 ? _o : type.fieldsCount, dnvType: 'dnvType' in data ? data.dnvType : type.dnvType, epCode: (_p = data.epCode) !== null && _p !== void 0 ? _p : type.epCode, ecType: (_q = data.ecType) !== null && _q !== void 0 ? _q : type.ecType, jointCommissionTitle: (_r = data.jointCommissionTitle) !== null && _r !== void 0 ? _r : type.jointCommissionTitle, serviceLineAbbrs: (_s = data.serviceLineAbbrs) !== null && _s !== void 0 ? _s : type.serviceLineAbbrs, updatedAt: new Date().toISOString() }) : type;
                });
                queryClient.setQueryData(COMPONENT_TYPES_QUERY_KEYS.list(), updatedTypes);
            }
            // Return a context object with the snapshotted value
            return { previousTypes };
        }),
        onSuccess: () => {
            toast.success('Component type updated successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_TYPES_QUERY_KEYS.all
            });
        },
        onError: (error, _variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousTypes) {
                queryClient.setQueryData(COMPONENT_TYPES_QUERY_KEYS.list(), context.previousTypes);
            }
            toast.error(error.message || 'Failed to update component type. Please try again.', { duration: 3000 });
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_TYPES_QUERY_KEYS.all
            });
        }
    });
}
// Hook to toggle component type active status (replaces delete functionality)
export function useToggleComponentTypeStatusMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => __awaiter(this, void 0, void 0, function* () {
            const response = yield toggleComponentTypeStatus(id);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (toggledId) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries({ queryKey: COMPONENT_TYPES_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousTypes = queryClient.getQueryData(COMPONENT_TYPES_QUERY_KEYS.list());
            // Optimistically toggle the active status
            if (previousTypes) {
                const updatedTypes = previousTypes.map((type) => type.id === toggledId
                    ? Object.assign(Object.assign({}, type), { active: !type.active, updatedAt: new Date().toISOString() }) : type);
                queryClient.setQueryData(COMPONENT_TYPES_QUERY_KEYS.list(), updatedTypes);
            }
            // Return a context object with the snapshotted value
            return { previousTypes };
        }),
        onSuccess: (data) => {
            const status = (data === null || data === void 0 ? void 0 : data.active) ? 'activated' : 'deactivated';
            toast.success(`Component type ${status} successfully!`);
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_TYPES_QUERY_KEYS.all
            });
        },
        onError: (error, _toggledId, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousTypes) {
                queryClient.setQueryData(COMPONENT_TYPES_QUERY_KEYS.list(), context.previousTypes);
            }
            toast.error(error.message || 'Failed to toggle component type status. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_TYPES_QUERY_KEYS.all
            });
        }
    });
}
// Hook to fetch recommended component types
export function useRecommendedComponentTypes() {
    return useQuery({
        queryKey: COMPONENT_TYPES_QUERY_KEYS.recommended(),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getRecommendedComponentTypes();
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data || [];
        }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3
    });
}
// Hook to import a component type from recommended list
export function useImportComponentTypeMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => __awaiter(this, void 0, void 0, function* () {
            const response = yield importRecommendedComponentType(data);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (newType) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches to avoid overwriting our optimistic import
            yield queryClient.cancelQueries({ queryKey: COMPONENT_TYPES_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousTypes = queryClient.getQueryData(COMPONENT_TYPES_QUERY_KEYS.list());
            // Optimistically update to the new value with temporary ID
            if (previousTypes) {
                const optimisticType = {
                    id: Date.now(), // Temporary ID
                    typeName: newType.typeName,
                    typeNameEn: newType.typeName,
                    typeNameEs: null,
                    typeNameFr: null,
                    description: null,
                    category: null,
                    active: true,
                    fieldDefinitions: null,
                    componentsCount: 0,
                    fieldsCount: 0,
                    editUrl: '',
                    deleteUrl: '',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    supportsIndividualReports: false
                };
                queryClient.setQueryData(COMPONENT_TYPES_QUERY_KEYS.list(), [...previousTypes, optimisticType]);
            }
            // Return a context object with the snapshotted value
            return { previousTypes };
        }),
        onSuccess: () => {
            toast.success(translate('recommended.component_type_imported_successfully', { namespace }));
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_TYPES_QUERY_KEYS.all
            });
        },
        onError: (error, _newType, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousTypes) {
                queryClient.setQueryData(COMPONENT_TYPES_QUERY_KEYS.list(), context.previousTypes);
            }
            const errorMessage = error.message || translate('recommended.import_component_type_error', { namespace });
            toast.error(errorMessage);
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_TYPES_QUERY_KEYS.all
            });
        }
    });
}
